import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * A piece of list state that lives in the URL query string.
 *
 * Filter, sort, search and the selected row describe *what you are looking
 * at*, so they belong in the URL: the view survives a refresh, can be pasted
 * to a colleague, and Back undoes the last change. Personal viewing
 * preferences (list vs grid, density) deliberately do NOT belong here — see
 * useViewMode — because a shared link shouldn't force someone into your
 * layout.
 *
 * React Router v5 has no useSearchParams (that arrived in v6), hence this.
 *
 * Two behaviours worth knowing:
 *
 *  - Values equal to `defaultValue` are removed from the query string, so the
 *    common case stays a clean URL and only deviations are spelled out.
 *  - `mode` picks the history entry. Deliberate changes (clicking a filter
 *    chip) should 'push' so Back means "undo that filter". Continuous ones
 *    (typing in a search box) must 'replace', or twelve keystrokes become
 *    twelve back-stack entries.
 */
export function useUrlState(key, defaultValue = '', { mode = 'push' } = {}) {
    const history = useHistory();
    const location = useLocation();

    const value = useMemo(() => {
        const raw = new URLSearchParams(location.search).get(key);
        return raw === null ? defaultValue : raw;
    }, [location.search, key, defaultValue]);

    const setValue = useCallback(
        (next) => {
            // Read the CURRENT query off history rather than the render-time
            // `location`: two setters firing in the same tick (e.g. changing a
            // filter also resetting the page) would otherwise each start from
            // the same stale snapshot and the second would drop the first.
            const current = history.location.search;
            const params = new URLSearchParams(current);
            const resolved =
                typeof next === 'function'
                    ? next(params.get(key) === null ? defaultValue : params.get(key))
                    : next;

            if (resolved === undefined || resolved === null || resolved === '' || resolved === defaultValue) {
                params.delete(key);
            } else {
                params.set(key, String(resolved));
            }

            const qs = params.toString();
            const target = `${history.location.pathname}${qs ? `?${qs}` : ''}`;
            if (target === `${history.location.pathname}${current}`) return; // no-op
            (mode === 'replace' ? history.replace : history.push)(target);
        },
        [history, key, defaultValue, mode]
    );

    return [value, setValue];
}

export default useUrlState;
